<template>
    <div>
        <loader v-if="isLoading" />
        <g-card v-else-if="visible" class="g-mt-5 onboarding-card">
            <g-row type="flex" justify="space-around">
                <g-column :span="24" class="g-mb-3">
                    <g-typography tag="p" font-weight="semibold" text-align="center" :value="title" />
                </g-column>

                <g-column :span="24" class="g-mb-6">
                    <g-typography tag="p" color="grey-6" text-align="center" :value="message" />
                </g-column>

                <g-column class="g-mb-6 onboarding-banner-button-column">
                    <g-button
                        class="onboarding-banner-button"
                        shape="round"
                        type="primary"
                        size="large"
                        @click="handleGetStarted()"
                    >
                        {{ onboardingButtonText }}
                    </g-button>
                </g-column>
            </g-row>
        </g-card>
    </div>
</template>

<script>
    import { ref, toRefs, computed, watch, onUpdated } from 'vue-demi';
    import { get } from 'lodash';
    import {
        ONBOARDING_BANNER_STEPS,
        STEP_TAX_INFO_ID,
        STEP_PAYOUT_METHOD_ID,
        SHOW_STEPS_QUERY_PARAM
    } from '../../../../../app-v2/constants/payouts';
    import {
        EVENT_NAME_PAYOUTS_TAX_SETUP_PROMPT_VIEWED,
        EVENT_NAME_PAYOUTS_TAX_SETUP_PROMPT_CLICKED
    } from '../../../../../app-v2/constants/analytics-events';
    import useContactStore from '../../../../../stores/contact';
    import useRoute from '../../../../composables/use-route';
    import useOnboarding from '../../../../composables/use-onboarding';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';

    export default {
        name: 'OnboardingBanner',

        props: {
            visible: {
                type: Boolean,
                default: true
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            payoutAccountCompleted: {
                type: Boolean,
                default: false
            }
        },

        setup(props, context) {
            const route = useRoute().get();
            const events = useEventTracking();
            const contact = useContactStore().data;
            const { hasStarted: hasStartedOnboarding } = useOnboarding();
            const { payoutAccountCompleted } = toRefs(props);

            const step = ref(STEP_TAX_INFO_ID);
            const determineStep = () => {
                if (payoutAccountCompleted.value) {
                    step.value = STEP_PAYOUT_METHOD_ID;
                }
            };
            determineStep();
            watch(payoutAccountCompleted, () => {
                determineStep();
            });

            const title = computed(() => {
                return ONBOARDING_BANNER_STEPS[step.value]?.title;
            });

            const message = computed(() => {
                return ONBOARDING_BANNER_STEPS[step.value]?.message;
            });

            const onboardingButtonText = computed(() => {
                return ONBOARDING_BANNER_STEPS[step.value]?.onboardingButtonText;
            });

            if (route.query[SHOW_STEPS_QUERY_PARAM]) {
                context.emit('open-onboarding-drawer', step.value);
            }

            const handleGetStarted = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_TAX_SETUP_PROMPT_CLICKED, getEventPayload());

                if (!hasStartedOnboarding()) {
                    context.emit('open-country-selection');
                } else {
                    context.emit('open-onboarding-drawer', step.value);
                }
            };

            const handleCreateOnboardingLink = () => {
                context.emit('redirect-to-stripe-connect');
            };

            const handleOpenCountrySelection = () => {
                context.emit('open-country-selection');
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    referrer: get(window.SERVER_DATA, 'referer', null)
                };
            };

            onUpdated(() => {
                if (!props.isLoading && props.visible) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_TAX_SETUP_PROMPT_VIEWED, getEventPayload());
                }
            });

            return {
                step,
                title,
                message,
                onboardingButtonText,

                handleGetStarted,
                handleCreateOnboardingLink,
                handleOpenCountrySelection
            };
        }
    };
</script>

<style lang="scss" scoped>
    .onboarding-card {
        border: solid 1px $g-color-grey-4;
        border-radius: $g-border-radius-md;
    }

    .onboarding-banner-button {
        background-color: #5c61ff;
        border: solid 1px #5c61ff;
        width: 100%;

        &:hover {
            background-color: #4246e7;
            border-color: #4246e7;
        }
    }

    @media only screen and (max-width: $g-screen-size-xs) {
        .onboarding-banner-button-column {
            width: 100%;
        }
    }
</style>
